import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { fmtHours } from "@/lib/utils"
import { router } from "@inertiajs/react"
import { format, parse } from "date-fns"
import { useEffect, useRef, useState } from "react"

interface ProjectOption {
  id: number
  name: string
  customer_name: string
}

export interface EditingEntry {
  id: number
  project_id: number | null
  description: string
  billable: boolean
  started_at: string | null
  stopped_at: string | null
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  startTime: string
  endTime: string
  calendarDate: string
  weekStart: string
  projects: ProjectOption[]
  editingEntry?: EditingEntry | null
  defaultProjectId?: string
}

function computeHours(startTime: string, endTime: string) {
  const [sh, sm] = startTime.split(":").map(Number)
  const [eh, em] = endTime.split(":").map(Number)
  const startMin = sh * 60 + sm
  const endMin = eh * 60 + em
  return Math.max((endMin - startMin) / 60, 0)
}

export default function CalendarCreateDialog({
  open,
  onOpenChange,
  startTime,
  endTime,
  calendarDate,
  weekStart,
  projects,
  editingEntry,
  defaultProjectId,
}: Props) {
  const isEditing = !!editingEntry
  const descriptionRef = useRef<HTMLInputElement>(null)
  const [projectId, setProjectId] = useState(defaultProjectId || "")
  const [description, setDescription] = useState("")
  const [billable, setBillable] = useState(true)
  const [editStart, setEditStart] = useState(startTime)
  const [editEnd, setEditEnd] = useState(endTime)
  const [humanize, setHumanize] = useState(false)
  const [startSec, setStartSec] = useState(0)
  const [endSec, setEndSec] = useState(0)
  const [submitting, setSubmitting] = useState(false)

  function randomSec() {
    return Math.floor(Math.random() * 50) + 5
  }

  // Sync editable times when props change (new drag selection)
  useEffect(() => {
    setEditStart(startTime)
    setEditEnd(endTime)
  }, [startTime, endTime])

  // Populate form when dialog opens
  useEffect(() => {
    if (!open) return
    if (editingEntry) {
      setProjectId(editingEntry.project_id ? String(editingEntry.project_id) : "")
      setDescription(editingEntry.description)
      setBillable(editingEntry.billable)
      setHumanize(false)
    } else {
      setProjectId(defaultProjectId || "")
      setDescription("")
      setBillable(true)
      setHumanize(false)
    }
  }, [open, editingEntry, defaultProjectId])

  // Autofocus description when creating a new entry (hours already set from drag)
  useEffect(() => {
    if (open && !editingEntry) {
      setTimeout(() => descriptionRef.current?.focus(), 0)
    }
  }, [open, editingEntry])

  // Roll new random seconds when humanize is toggled on
  useEffect(() => {
    if (humanize) {
      setStartSec(randomSec())
      setEndSec(randomSec())
    } else {
      setStartSec(0)
      setEndSec(0)
    }
  }, [humanize])

  const hours = computeHours(editStart, editEnd)

  function resetForm() {
    setProjectId("")
    setDescription("")
    setBillable(true)
    setHumanize(false)
    setSubmitting(false)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)

    const ss = String(startSec).padStart(2, "0")
    const es = String(endSec).padStart(2, "0")

    const startedAt = new Date(`${calendarDate}T${editStart}:${ss}`).toISOString()
    const stoppedAt = new Date(`${calendarDate}T${editEnd}:${es}`).toISOString()

    const timeEntryData = {
      project_id: projectId && projectId !== "none" ? projectId : null,
      description,
      entry_date: calendarDate,
      hours: hours.toFixed(2),
      billable,
      started_at: startedAt,
      stopped_at: stoppedAt,
    }

    const callbacks = {
      onSuccess: () => {
        onOpenChange(false)
        resetForm()
      },
      onFinish: () => setSubmitting(false),
    }

    if (isEditing) {
      router.patch(
        `/time_entries/${editingEntry!.id}`,
        { time_entry: timeEntryData, view: "calendar", date: weekStart },
        callbacks,
      )
    } else {
      router.post("/time_entries", { time_entry: timeEntryData, view: "calendar", date: weekStart }, callbacks)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Time Entry" : "Log Time Entry"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="text-muted-foreground text-xs">
            {format(parse(calendarDate, "yyyy-MM-dd", new Date()), "EEEE, MMMM d")}
          </div>

          <div className="grid grid-cols-[1fr_auto_1fr_auto] items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Start</Label>
              <div className="flex items-center gap-1">
                <Input
                  type="time"
                  step={humanize ? 1 : 60}
                  value={humanize ? `${editStart}:${String(startSec).padStart(2, "0")}` : editStart}
                  onChange={(e) => {
                    const val = e.target.value
                    setEditStart(val.slice(0, 5))
                    if (humanize && val.length > 5) setStartSec(Number(val.slice(6)) || 0)
                  }}
                />
              </div>
            </div>
            <span className="text-muted-foreground pb-2">to</span>
            <div className="space-y-1">
              <Label className="text-xs">End</Label>
              <div className="flex items-center gap-1">
                <Input
                  type="time"
                  step={humanize ? 1 : 60}
                  value={humanize ? `${editEnd}:${String(endSec).padStart(2, "0")}` : editEnd}
                  onChange={(e) => {
                    const val = e.target.value
                    setEditEnd(val.slice(0, 5))
                    if (humanize && val.length > 5) setEndSec(Number(val.slice(6)) || 0)
                  }}
                />
              </div>
            </div>
            <span className="text-muted-foreground pb-2 text-sm">{fmtHours(hours)}</span>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Project</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger>
                <SelectValue placeholder="Select project..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Project</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.customer_name ? `${p.customer_name} — ` : ""}
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Description</Label>
            <Input
              ref={descriptionRef}
              placeholder="What did you work on?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={billable}
                onChange={(e) => setBillable(e.target.checked)}
                className="rounded"
              />
              Billable
            </label>
            <label
              className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer"
              title="Add random seconds to start/end times"
            >
              <input
                type="checkbox"
                checked={humanize}
                onChange={(e) => setHumanize(e.target.checked)}
                className="rounded"
              />
              Girazize
            </label>
          </div>

          <DialogFooter>
            <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={hours <= 0 || submitting}>
              {submitting ? "Saving..." : isEditing ? "Update Entry" : "Save Entry"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
